import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Users, TrendingUp, Zap } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-chart-tooltip">
        <p className="label">{`Time: ${label}`}</p>
        <p className="value">
          <span style={{ color: '#34d399', fontWeight: 800 }}>{payload[0].value}</span> Viewers
        </p>
      </div>
    );
  }
  return null;
};

export const ActiveViewersChart = ({ dataHistory, currentViewers }) => {
  // Compute chart insights
  const viewerValues = dataHistory.map(d => d.activeViewers);
  const maxViewers = viewerValues.length > 0 ? Math.max(...viewerValues) : currentViewers;
  const avgViewers = viewerValues.length > 0
    ? (viewerValues.reduce((a, b) => a + b, 0) / viewerValues.length).toFixed(1)
    : currentViewers;

  const previousVal = viewerValues.length > 1 ? viewerValues[viewerValues.length - 2] : currentViewers;
  const diff = currentViewers - previousVal;

  return (
    <div className="glass-card" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-emerald)'
          }}>
            <Users size={22} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#ffffff' }}>
              Current Viewers
            </h2>
          </div>
        </div>

        {/* Quick Viewers Stats Ribbon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>

          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current</span>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#34d399', lineHeight: '1.2' }}>
              {currentViewers} <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-muted)' }}>users</span>
            </div>
          </div>

          <div style={{ height: '30px', width: '1px', background: 'rgba(16, 185, 129, 0.2)' }} />

          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Peak</span>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffffff', lineHeight: '1.2' }}>
              {maxViewers}
            </div>
          </div>

          <div style={{ height: '30px', width: '1px', background: 'rgba(16, 185, 129, 0.2)' }} />

          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Average</span>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--accent-light)', lineHeight: '1.2' }}>
              {avgViewers}
            </div>
          </div>

          {diff !== 0 && (
            <span className={`badge-status ${diff > 0 ? 'healthy' : ''}`} style={{ fontSize: '0.72rem' }}>
              <TrendingUp size={12} style={{ transform: diff < 0 ? 'rotate(180deg)' : 'none' }} />
              {diff > 0 ? `+${diff}` : diff}
            </span>
          )}

        </div>

      </div>

      {/* Recharts Area Chart Container */}
      <div style={{ width: '100%', height: 260, marginTop: '10px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={dataHistory}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="emeraldGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.45} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(16, 185, 129, 0.1)" vertical={false} />
            <XAxis
              dataKey="time"
              stroke="#6b7280"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'rgba(16, 185, 129, 0.2)' }}
            />
            <YAxis
              stroke="#6b7280"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'rgba(16, 185, 129, 0.2)' }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="activeViewers"
              stroke="#10b981"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#emeraldGradient)"
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{
        display: 'flex',
        justify: 'space-between',
        alignItems: 'center',
        fontSize: '0.72rem',
        color: 'var(--text-dim)',
        marginTop: '12px',
        borderTop: '1px solid rgba(16, 185, 129, 0.1)',
        paddingTop: '8px'
      }}>
        <span><Zap size={12} color="var(--primary-emerald)" inline="true" /> Live data streamed from workerVitals.activeViewers</span>
        <span>Showing last {dataHistory.length} polling snapshots</span>
      </div>
    </div>
  );
};
